/* ==========================================================================
   SKOCKZ scroll reveal — assets/skx-reveal.js
   Watches the page and adds .skx-rv-in to each piece of content as it scrolls
   into view. All the timing lives in CSS custom properties (see
   snippets/skx-reveal.liquid), so this file only decides WHEN and in WHAT
   ORDER things arrive, never how they look.

   Why IntersectionObserver and not GSAP ScrollTrigger: Horizon scrolls
   div.page-wrapper, not the window, so every ScrollTrigger needs an explicit
   scroller or it silently never fires. IntersectionObserver measures against
   the viewport, so it does not care which element does the scrolling — and it
   adds 0 KB to a store on the Basic plan.
   ========================================================================== */

(function () {
  'use strict';

  /* Tells the failsafe timer in the head snippet that the reveal is alive.
     If this file ever fails to load, that timer strips html.skx-rv and every
     hidden element comes straight back. */
  window.__skxRevealReady = true;

  var html = document.documentElement;
  if (!html.classList.contains('skx-rv')) return;
  if (!('IntersectionObserver' in window)) {
    html.classList.remove('skx-rv');
    return;
  }

  /* Cards stagger against their neighbours; blocks arrive on their own. */
  var CARDS =
    '.skx-card,.skc-card,.skxr__card,.skr__card,.product-grid__card,.resource-list__item';
  var BLOCKS =
    '.skx-head,.skc-head,.skxr__head,.skr__head,.skl__head,.skl__stage,.skl__panel,.skp__panel,.skb__panel,' +
    '.section-content-wrapper > .shopify-block,.section-content-wrapper > .group-block';
  var SEL = CARDS + ',' + BLOCKS;

  /* Kept in step with the scope in skx-reveal.css. */
  var SCOPES = '#MainContent,.page-wrapper > footer';

  /* Popups, dialogs and carousels move their own children around; animating
     anything inside them fights whatever they are already doing. */
  var SKIP_INSIDE =
    '#skz-popspin,[data-skx-reveal="off"],dialog,slideshow-component,' +
    '.announcement-bar,.predictive-search,.quick-add-modal';

  var animateFirstScreen = html.getAttribute('data-skx-rv-first') === 'true';
  var tracked = new WeakSet();
  var firstPass = true;

  var io = new IntersectionObserver(
    function (entries) {
      var hits = [];
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        io.unobserve(entries[i].target);
        hits.push(entries[i].target);
      }
      if (!hits.length) return;

      /* Every layout READ first, then every WRITE. Mixing them would force a
         re-layout per card, which on a sixteen-card grid is the difference
         between one measurement and sixteen. */
      var cols = columnIndexes(hits);
      for (var j = 0; j < hits.length; j++) {
        hits[j].style.setProperty('--skx-rv-i', cols[j]);
        hits[j].classList.add('skx-rv-in');
      }
    },
    {
      /* Fires a little before the element clears the bottom edge, which gives
         the delay somewhere to run — by the time the card is properly in front
         of you it is already moving, instead of sitting blank for half a
         second. Raise this if the delay is turned up past ~600ms. */
      rootMargin: '0px 0px 8% 0px',
      threshold: 0
    }
  );

  /* Stagger by ROW, not by position in the whole grid. Eight cards indexed
     0..7 would make the last one wait 8 x stagger after the base delay; what
     you actually want is each row rippling across its own columns. Cards
     sharing an offsetTop are one row. Capped at 5 so an unusually wide grid
     cannot run away.

     Worked out fresh on every callback rather than cached on the parent: a map
     built while the grid was still one column (webfonts and images not settled
     yet) would hand every card index 0 and quietly kill the stagger for the
     rest of the page. Reads inside one callback are cheap because nothing
     writes between them. */
  function columnIndexes(els) {
    var byParent = new Map();
    var out = [];
    for (var i = 0; i < els.length; i++) {
      var parent = els[i].parentElement;
      if (!parent) { out.push(0); continue; }
      var map = byParent.get(parent);
      if (!map) {
        map = buildRows(parent);
        byParent.set(parent, map);
      }
      out.push(map.get(els[i]) || 0);
    }
    return out;
  }

  function buildRows(parent) {
    var map = new WeakMap();
    var rows = {};
    var kids = parent.children;
    for (var i = 0; i < kids.length; i++) {
      if (!kids[i].matches || !kids[i].matches(SEL)) continue;
      var row = Math.round(kids[i].offsetTop / 8);
      var n = rows[row] || 0;
      map.set(kids[i], n > 5 ? 5 : n);
      rows[row] = n + 1;
    }
    return map;
  }

  function add(el) {
    if (tracked.has(el)) return;
    tracked.add(el);

    if (el.closest(SKIP_INSIDE)) return skip(el);

    var isCard = el.matches(CARDS);
    var parent = el.parentElement;

    /* A card inside another card, or a block inside another block, would
       animate twice over — once with its parent and once on its own. */
    if (parent && parent.closest(isCard ? CARDS : BLOCKS)) return skip(el);

    /* A block wrapping a grid of cards hands over to the cards, so the grid
       ripples instead of the whole panel sliding up in one lump. */
    if (!isCard && el.querySelector(CARDS)) return skip(el);

    /* A sticky column must not be transformed — that makes it a containing
       block and the pinning goes strange. */
    if (getComputedStyle(el).position === 'sticky') return skip(el);

    /* Anything already on screen when the page loaded is shown immediately:
       a visitor should never land on a blank first screen, and the hero image
       stays out of the animation so LCP is untouched. */
    if (firstPass && !animateFirstScreen && el.getBoundingClientRect().top < window.innerHeight) {
      el.classList.add('skx-rv-now');
      return;
    }

    io.observe(el);
  }

  function skip(el) {
    el.classList.add('skx-rv-skip');
  }

  function scan() {
    var scopes = document.querySelectorAll(SCOPES);
    for (var s = 0; s < scopes.length; s++) {
      var els = scopes[s].querySelectorAll(SEL);
      for (var i = 0; i < els.length; i++) add(els[i]);
    }
    firstPass = false;
  }

  scan();

  /* Sections re-render constantly here: the theme editor, variant swaps,
     collection filtering, "load more". Anything new gets picked up on the next
     tick. Only childList is watched, so the class and style writes above
     cannot loop back round. */
  var pending = 0;
  function schedule() {
    if (pending) return;
    pending = setTimeout(function () {
      pending = 0;
      scan();
    }, 150);
  }

  var mo = new MutationObserver(schedule);
  var scopes = document.querySelectorAll(SCOPES);
  for (var s = 0; s < scopes.length; s++) {
    mo.observe(scopes[s], { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', schedule);
})();
